import { HocuspocusProvider, HocuspocusProviderWebsocket } from "@hocuspocus/provider";
import * as Y from "yjs";
import { changesMap, TEXT_KEY } from "../editor/collab";
import { REVIEW_ORIGIN } from "../editor/review";
import type { User } from "../lib/api";
import { toast, userColor } from "../lib/app";
import { dropDoc, dropLeftovers, leftoverDocs, leftovers, Outbox, type Leftover } from "./outbox";

export interface DocHandle {
  docId: string;
  ydoc: Y.Doc;
  ytext: Y.Text;
  provider: HocuspocusProvider;
  undoManager: Y.UndoManager;
  synced: Promise<void>;
  isSynced: boolean;
  lastUsed: number;
  /** Local edits the server hasn't acknowledged yet. */
  outbox: Outbox;
  /** Edits from an earlier visit, replayed into this doc and waiting for the server. */
  adopted: Leftover[];
  replayed: boolean;
  readOnly: boolean;
  /** Opened only to send edits left from an earlier visit; closed once they're saved. */
  background: boolean;
}

export interface PresenceState {
  clientId: number;
  /** Claude, while it writes for someone through Claude Code, has a negative id and `claude` set. */
  user: { id: number; name: string; color: string; claude?: boolean; for?: string };
  docId?: string | null;
}

type EventListener = (event: Record<string, unknown> & { type: string }) => void;

const MAX_OPEN_DOCS = 8;
/** How long leaving a project (in-app) waits for unsaved edits to reach the server. */
const LEAVE_GRACE_MS = 15_000;
const LEFTOVER_ORIGIN = Symbol("leftover");
/** The server closes sockets nobody is using with this code (IDLE_CLOSE_CODE in server/src/idle.ts). */
const IDLE_CLOSE_CODE = 4100;
/** While someone is using the page, tell the server at most this often. */
const ACTIVE_EVERY_MS = 60_000;
const ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "focus"];

/**
 * One websocket per open project, multiplexing the project channel (presence
 * and server events) and every open file.
 */
export class ProjectSession {
  readonly socket: HocuspocusProviderWebsocket;
  readonly channel: HocuspocusProvider;
  private docs = new Map<string, DocHandle>();
  private listeners = new Set<EventListener>();
  private statusListeners = new Set<(s: string) => void>();
  private pendingListeners = new Set<() => void>();
  private wasPending = false;
  private currentDoc: string | null = null;
  private toldRestored = false;
  private lastActiveSent = 0;
  /** When someone last used the page. Opening the project counts. */
  private lastUsed = Date.now();
  /** Names this tab to the server, which keeps its idle time across reconnects (see server/src/idle.ts). */
  private readonly tab = Math.random().toString(36).slice(2, 12);
  /** "connecting", "connected" or "disconnected" from the socket, or "away" after an idle disconnect. */
  status = "connecting";

  constructor(
    readonly projectId: string,
    readonly user: User,
  ) {
    const proto = location.protocol === "https:" ? "wss:" : "ws:";
    this.socket = new HocuspocusProviderWebsocket({ url: `${proto}//${location.host}/ws?tab=${this.tab}` });
    this.socket.on("status", ({ status }: { status: string }) => {
      const was = this.status;
      this.setStatus(status);
      // A new socket carries on from this tab's idle time, so say so if someone is here (back from away, say).
      if (status === "connected" && was !== "connected" && Date.now() - this.lastUsed < ACTIVE_EVERY_MS) this.sendActive();
    });
    this.socket.on("close", ({ event }: { event: { code?: number } }) => {
      if (event?.code !== IDLE_CLOSE_CODE) return;
      // Stay disconnected, so the server can sleep, until someone uses the page again.
      this.socket.disconnect();
      this.setStatus("away");
    });
    for (const e of ACTIVITY_EVENTS) window.addEventListener(e, this.onActivity, { capture: true, passive: true });
    document.addEventListener("visibilitychange", this.onActivity);
    this.channel = new HocuspocusProvider({
      websocketProvider: this.socket,
      name: `project.${projectId}`,
      token: "session",
      onStateless: ({ payload }) => {
        try {
          const event = JSON.parse(payload);
          for (const fn of this.listeners) fn(event);
        } catch {}
      },
    });
    this.channel.attach();
    this.channel.awareness?.setLocalStateField("user", { id: user.id, name: user.displayName, color: userColor(user.id) });
    // Edits left from an earlier visit (say the tab was closed while offline) go up as soon as we connect.
    for (const docId of leftoverDocs(projectId)) this.openDoc(docId, true);
  }

  onEvent(fn: EventListener) {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  onStatus(fn: (s: string) => void) {
    this.statusListeners.add(fn);
    return () => this.statusListeners.delete(fn);
  }

  private setStatus(status: string) {
    this.status = status;
    for (const fn of this.statusListeners) fn(status);
  }

  /** Someone is using the page: reconnect if we dropped while they were away, or let the server know they're here. */
  private onActivity = () => {
    if (document.visibilityState === "hidden") return;
    const now = Date.now();
    this.lastUsed = now;
    if (this.status === "away") {
      void this.socket.connect();
      return;
    }
    if (this.status !== "connected" || now - this.lastActiveSent < ACTIVE_EVERY_MS) return;
    this.sendActive();
  };

  private sendActive() {
    this.lastActiveSent = Date.now();
    this.channel.sendStateless(JSON.stringify({ type: "active" }));
  }

  setCurrentDoc(docId: string | null) {
    this.currentDoc = docId;
    this.channel.awareness?.setLocalStateField("docId", docId);
  }

  presence(): PresenceState[] {
    const states = this.channel.awareness?.getStates() ?? new Map();
    const out: PresenceState[] = [];
    states.forEach((s: Record<string, unknown>, clientId: number) => {
      if (s.user) out.push({ clientId, user: s.user as PresenceState["user"], docId: s.docId as string | null });
    });
    return out;
  }

  onPresence(fn: () => void) {
    const aw = this.channel.awareness;
    aw?.on("change", fn);
    return () => aw?.off("change", fn);
  }

  /**
   * Where someone's cursor is in the file they have open. Opens that file to
   * find out, and gives their cursor a moment to arrive; null if it doesn't.
   */
  cursorOf(p: PresenceState, waitMs = 3000): Promise<{ line: number; column: number } | null> {
    if (!p.docId) return Promise.resolve(null);
    const h = this.openDoc(p.docId);
    const aw = h.provider.awareness;
    if (!aw) return Promise.resolve(null);
    const find = () => {
      let at: number | null = null;
      aw.getStates().forEach((s) => {
        if (s.user?.id !== p.user.id || !s.cursor?.head) return;
        const abs = Y.createAbsolutePositionFromRelativePosition(Y.createRelativePositionFromJSON(s.cursor.head), h.ydoc);
        if (!abs || abs.type !== h.ytext) return;
        // The same person can have the file open in several tabs; prefer the one in the presence list.
        if (at === null || s.user.tab === p.clientId) at = abs.index;
      });
      return at;
    };
    return new Promise((resolve) => {
      const finish = (at: number | null) => {
        clearTimeout(timer);
        aw.off("change", check);
        if (at === null) return resolve(null);
        const before = h.ytext.toString().slice(0, at);
        resolve({ line: before.split("\n").length, column: at - before.lastIndexOf("\n") - 1 });
      };
      const check = () => {
        const at = h.isSynced ? find() : null;
        if (at !== null) finish(at);
      };
      const timer = setTimeout(() => finish(null), waitMs);
      aw.on("change", check);
      h.synced.then(check);
    });
  }

  /** Open (or reuse) a live doc. */
  openDoc(docId: string, background = false): DocHandle {
    const existing = this.docs.get(docId);
    if (existing) {
      existing.lastUsed = Date.now();
      if (!background) existing.background = false;
      return existing;
    }
    const ydoc = new Y.Doc();
    const ytext = ydoc.getText(TEXT_KEY);
    let resolveSynced!: () => void;
    const synced = new Promise<void>((r) => (resolveSynced = r));
    const handle: DocHandle = {
      docId,
      ydoc,
      ytext,
      synced,
      isSynced: false,
      lastUsed: Date.now(),
      outbox: new Outbox(this.projectId, docId),
      adopted: [],
      replayed: false,
      readOnly: false,
      background,
      // Track the review map too, so undoing a tracked edit also removes its suggestion, and accept/reject can be undone.
      undoManager: new Y.UndoManager([ytext, changesMap(ydoc)], { trackedOrigins: new Set([REVIEW_ORIGIN]) }),
      provider: new HocuspocusProvider({
        websocketProvider: this.socket,
        name: `doc.${this.projectId}.${docId}`,
        document: ydoc,
        token: "session",
        onAuthenticated: ({ scope }) => {
          handle.readOnly = scope === "readonly";
        },
        onAuthenticationFailed: ({ reason }) => {
          // The doc was deleted, so there's nowhere to save to. Anything else (signed out, say) keeps the edits.
          if (reason !== "unknown-document") return;
          dropDoc(this.projectId, docId);
          handle.outbox.clear();
          this.notifyPending();
          if (handle.background) this.closeLater(handle);
        },
        onSynced: () => {
          handle.isSynced = true;
          resolveSynced();
          this.replayLeftovers(handle);
        },
        // The server acknowledges every update, so zero means all our edits are in its copy.
        onUnsyncedChanges: ({ number }) => {
          if (number === 0) this.saved(handle);
        },
      }),
    };
    // Every local change (typing, comments, accept/reject, undo) counts until the server acknowledges it.
    ydoc.on("update", (update: Uint8Array, origin: unknown) => {
      if (origin === handle.provider || handle.readOnly) return;
      handle.outbox.add(update);
      this.notifyPending();
    });
    handle.provider.attach();
    handle.provider.awareness?.setLocalStateField("user", {
      // id and tab tie this cursor to an entry in the project's presence list (see cursorOf).
      id: this.user.id,
      tab: this.channel.awareness?.clientID,
      name: this.user.displayName,
      color: userColor(this.user.id),
      colorLight: userColor(this.user.id) + "33",
    });
    this.docs.set(docId, handle);
    this.evict(docId);
    return handle;
  }

  /** Apply edits that earlier visits left in local storage, once we hold the server's copy. */
  private replayLeftovers(h: DocHandle) {
    if (h.replayed || h.readOnly) return;
    h.replayed = true;
    const entries = leftovers(this.projectId, h.docId);
    let changed = false;
    const mark = () => (changed = true);
    h.ydoc.on("update", mark);
    for (const e of entries) Y.applyUpdate(h.ydoc, e.update, LEFTOVER_ORIGIN);
    h.ydoc.off("update", mark);
    if (changed) {
      h.adopted = entries;
      if (!this.toldRestored) toast("Saving edits from your last visit that hadn't reached the server");
      this.toldRestored = true;
    } else {
      // Already on the server (or built on text that no longer exists).
      dropLeftovers(entries);
      if (h.background) this.closeLater(h);
    }
  }

  private saved(h: DocHandle) {
    if (h.outbox.pending) {
      h.outbox.clear();
      this.notifyPending();
    }
    if (h.adopted.length) {
      dropLeftovers(h.adopted);
      h.adopted = [];
    }
    if (h.background) this.closeLater(h);
  }

  /** Close a background doc outside the provider callback that finished with it. */
  private closeLater(h: DocHandle) {
    setTimeout(() => {
      if (this.docs.get(h.docId) === h && h.background && h.docId !== this.currentDoc && !h.outbox.pending) this.closeDoc(h.docId);
    });
  }

  private evict(keep: string) {
    if (this.docs.size <= MAX_OPEN_DOCS) return;
    const oldest = [...this.docs.values()]
      .filter((d) => d.docId !== keep && d.docId !== this.currentDoc && !d.outbox.pending)
      .sort((a, b) => a.lastUsed - b.lastUsed)[0];
    if (oldest) this.closeDoc(oldest.docId);
  }

  closeDoc(docId: string) {
    const h = this.docs.get(docId);
    if (!h) return;
    this.docs.delete(docId);
    // Anything still unsaved stays in local storage and is replayed next time the doc opens.
    h.outbox.dispose();
    h.provider.destroy();
    h.undoManager.destroy();
    h.ydoc.destroy();
    for (const fn of this.listeners) fn({ type: "doc:closed", docId });
    this.notifyPending();
  }

  getDoc(docId: string): DocHandle | undefined {
    return this.docs.get(docId);
  }

  openDocs(): DocHandle[] {
    return [...this.docs.values()];
  }

  /** True when any open doc has local edits the server hasn't acknowledged yet. */
  hasUnsynced(): boolean {
    return [...this.docs.values()].some((d) => d.outbox.pending);
  }

  /** Called when hasUnsynced() flips. */
  onPendingChange(fn: () => void) {
    this.pendingListeners.add(fn);
    return () => this.pendingListeners.delete(fn);
  }

  private notifyPending() {
    const pending = this.hasUnsynced();
    if (pending === this.wasPending) return;
    this.wasPending = pending;
    for (const fn of [...this.pendingListeners]) fn();
  }

  /** Disconnect, but first give unsaved edits a few seconds to reach the server (leaving the project in-app). */
  destroy() {
    for (const e of ACTIVITY_EVENTS) window.removeEventListener(e, this.onActivity, { capture: true });
    document.removeEventListener("visibilitychange", this.onActivity);
    this.listeners.clear();
    this.statusListeners.clear();
    this.channel.awareness?.setLocalState(null);
    for (const h of this.docs.values()) h.provider.awareness?.setLocalState(null);
    let done = false;
    let timer: number | undefined;
    const finish = () => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      this.pendingListeners.clear();
      for (const id of [...this.docs.keys()]) this.closeDoc(id);
      this.channel.destroy();
      this.socket.destroy();
    };
    if (!this.hasUnsynced()) return finish();
    timer = window.setTimeout(finish, LEAVE_GRACE_MS);
    this.onPendingChange(() => {
      if (!this.hasUnsynced()) finish();
    });
  }
}
